// Same limits as the api, which rejects a longer label or a longer list.
export const TASK_LABEL_MAX_LENGTH = 80;
export const TASKS_MAX_COUNT = 50;
// The add field takes more than one task: the AI check splits it.
export const TASK_INPUT_MAX_LENGTH = 100;
// The api never returns more items for one input.
const TASK_CHECK_ITEMS_MAX_COUNT = 10;

export const TASK_SOURCE = {
    QUALIFICATION: "qualification",
    COMPANY: "company",
};

// Duplicate key only, never stored. Kept identical in api
// (missionTemplateHelpers.js) and app.admin (templateFormData.js).
export const normalizeTaskLabel = (label) =>
    String(label ?? "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

/** The label as the api stores it: trimmed, inner spaces collapsed. */
export const cleanTaskLabel = (label) =>
    typeof label === "string" ? label.trim().replace(/\s+/g, " ") : "";

/**
 * The qualification picked at step 1, as loaded for the dropdown. A name can
 * exist in several sectors: the sector decides when it is known.
 */
export const findQualification = (qualifications, name, type) => {
    if (!Array.isArray(qualifications) || !name) return null;
    const candidates = qualifications.filter((q) => q?.name === name);
    return (
        (type && candidates.find((q) => q.type === type)) ||
        candidates[0] ||
        null
    );
};

/** What a qualification suggests: its non archived tasks, sorted by `order`. */
export const getQualificationTaskProposals = (qualification) => {
    const tasks = Array.isArray(qualification?.tasks)
        ? qualification.tasks
        : [];
    return tasks
        .filter((task) => task && !task.archived && cleanTaskLabel(task.label))
        .slice()
        .sort(
            (a, b) =>
                (a.order ?? Number.MAX_SAFE_INTEGER) -
                (b.order ?? Number.MAX_SAFE_INTEGER)
        )
        .map((task) => ({
            qualificationTaskId: task._id ? String(task._id) : "",
            label: cleanTaskLabel(task.label),
        }));
};

let companyTaskSeq = 0;
const nextCompanyTaskKey = () => {
    companyTaskSeq += 1;
    return `company-${companyTaskSeq}`;
};

const qualificationRowKey = (task) =>
    task.qualificationTaskId
        ? `qualification-${task.qualificationTaskId}`
        : `qualification-label-${normalizeTaskLabel(task.label)}`;

/**
 * Check state of an added task: the AI check runs after the row is shown.
 * Lives on the row only, never in the payload.
 */
export const TASK_CHECK_STATUS = {
    PENDING: "pending",
    VERIFIED: "verified",
};

// Same delay as the facade call, so saving never waits longer than the check.
export const TASK_CHECK_TIMEOUT_MS = 20000;

// Context sent with the check: the api caps it at 120 characters.
const TASK_CHECK_JOB_TITLE_MAX_LENGTH = 120;

const companyRow = (label, checkStatus = TASK_CHECK_STATUS.VERIFIED) => ({
    key: nextCompanyTaskKey(),
    label,
    source: TASK_SOURCE.COMPANY,
    checked: true,
    checkStatus,
});

const qualificationRow = (task, checked) => ({
    key: qualificationRowKey(task),
    label: task.label,
    source: TASK_SOURCE.QUALIFICATION,
    qualificationTaskId: task.qualificationTaskId || "",
    checked,
});

// A label already listed is dropped: the first row wins, as in the api.
const withoutDuplicateLabels = (rows) => {
    const seen = new Set();
    return rows.filter((row) => {
        const key = normalizeTaskLabel(row.label);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

/**
 * Rows of the Tasks step (`templateData.tasks`): the tasks added by the
 * company first, in display order, then the qualification tasks.
 */
export const buildTaskRows = ({ proposals = [], templateTasks = [] } = {}) => {
    const stored = (Array.isArray(templateTasks) ? templateTasks : [])
        .map((task) => ({
            label: cleanTaskLabel(task?.label),
            source: task?.source,
            qualificationTaskId: task?.qualificationTaskId
                ? String(task.qualificationTaskId)
                : "",
        }))
        .filter((task) => task.label);

    // A template without tasks behaves like a new one: everything checked.
    if (stored.length === 0) {
        return withoutDuplicateLabels(
            proposals.map((task) => qualificationRow(task, true))
        );
    }

    const companyRows = stored
        .filter((task) => task.source === TASK_SOURCE.COMPANY)
        .map((task) => companyRow(task.label));
    const storedQualificationTasks = stored.filter(
        (task) => task.source !== TASK_SOURCE.COMPANY
    );
    const storedById = new Map(
        storedQualificationTasks
            .filter((task) => task.qualificationTaskId)
            .map((task) => [task.qualificationTaskId, task])
    );
    const storedLabelKeys = new Set(
        storedQualificationTasks
            .filter((task) => !task.qualificationTaskId)
            .map((task) => normalizeTaskLabel(task.label))
    );
    const matched = new Set();
    const proposalRows = proposals.map((task) => {
        const byId =
            task.qualificationTaskId && storedById.get(task.qualificationTaskId);
        if (byId) {
            matched.add(byId);
            // The template keeps the label it was saved with.
            return qualificationRow({ ...task, label: byId.label }, true);
        }
        const labelKey = normalizeTaskLabel(task.label);
        const byLabel = storedLabelKeys.has(labelKey);
        if (byLabel) {
            matched.add(
                storedQualificationTasks.find(
                    (stored) =>
                        !stored.qualificationTaskId &&
                        normalizeTaskLabel(stored.label) === labelKey
                )
            );
        }
        return qualificationRow(task, byLabel);
    });
    // Saved tasks the qualification no longer offers (archived, removed)
    // stay listed and checked, so saving again does not drop them silently.
    const keptRows = storedQualificationTasks
        .filter((task) => !matched.has(task))
        .map((task) => qualificationRow(task, true));

    return withoutDuplicateLabels([...companyRows, ...proposalRows, ...keptRows]);
};

/** New qualification: its proposals replace the old ones, all checked; added tasks stay. */
export const replaceProposedTasks = (rows, proposals) =>
    withoutDuplicateLabels([
        ...(rows || []).filter((row) => row.source === TASK_SOURCE.COMPANY),
        ...proposals.map((task) => qualificationRow(task, true)),
    ]);

export const ADD_TASK_ERROR = {
    EMPTY: "empty",
    TOO_MANY: "tooMany",
};

/**
 * The one message under the add field (`templateData.taskNotice`): any new
 * notice replaces the previous one.
 * - TICKED / ALREADY_LISTED: `{ type, match, addLabel, tickedKey }`, where
 *   `addLabel` (set only after an `existing` answer) feeds the "add as a new
 *   task" link and `tickedKey` is the proposed row the notice ticked.
 * - REJECTED / UNCHECKED_TOO_LONG: `{ type, label }`. TOO_MANY: `{ type }`.
 * - MULTIPLE: `{ type, lines }`, after an input split into several tasks:
 *   one line per task not simply added, each line shaped as one of the
 *   notices above plus an `id`.
 */
export const TASK_NOTICE = {
    TICKED: "ticked",
    ALREADY_LISTED: "alreadyListed",
    REJECTED: "rejected",
    TOO_MANY: "tooMany",
    UNCHECKED_TOO_LONG: "uncheckedTooLong",
    MULTIPLE: "multiple",
};

/** The grey banners; the other notices are errors. */
export const isTaskBanner = (notice) =>
    notice?.type === TASK_NOTICE.TICKED ||
    notice?.type === TASK_NOTICE.ALREADY_LISTED ||
    notice?.type === TASK_NOTICE.MULTIPLE;

// The api caps the list sent with the check.
const TASK_CHECK_EXISTING_MAX_COUNT = 100;

const findRowByLabel = (rows, label, exceptKey) => {
    const key = normalizeTaskLabel(label);
    if (!key) return null;
    return (
        (rows || []).find(
            (row) =>
                row.key !== exceptKey && normalizeTaskLabel(row.label) === key
        ) || null
    );
};

const isUncheckedProposal = (row) =>
    row?.source === TASK_SOURCE.QUALIFICATION && !row.checked;

const setProposalChecked = (rows, key, checked) =>
    rows.map((row) =>
        row.key === key && row.source === TASK_SOURCE.QUALIFICATION
            ? { ...row, checked }
            : row
    );

/**
 * The typed task is already `target`: an unchecked proposal gets checked,
 * anything else stays as is. `rows` must not hold the typed task any more.
 */
const markExistingTask = (rows, target, addLabel = null) => {
    const tick = isUncheckedProposal(target);
    return {
        rows: tick ? setProposalChecked(rows, target.key, true) : rows,
        notice: {
            type: tick ? TASK_NOTICE.TICKED : TASK_NOTICE.ALREADY_LISTED,
            match: target.label,
            addLabel,
            tickedKey: tick ? target.key : null,
        },
    };
};

/**
 * Returns `{ rows, key, label }` with the new row first and pending its AI
 * check, `{ rows, notice }` when the label is already listed (no AI call), or
 * `{ error }` when the label cannot be added.
 */
export const addCompanyTask = (rows, label) => {
    const cleaned = cleanTaskLabel(label).slice(0, TASK_INPUT_MAX_LENGTH);
    if (!cleaned) return { error: ADD_TASK_ERROR.EMPTY };
    const current = rows || [];
    const tooMany = countSelectedTasks(current) >= TASKS_MAX_COUNT;
    const listed = findRowByLabel(current, cleaned);
    if (listed) {
        // Ticking adds one task to the payload, which the api caps.
        if (isUncheckedProposal(listed) && tooMany) {
            return { error: ADD_TASK_ERROR.TOO_MANY };
        }
        return markExistingTask(current, listed);
    }
    if (tooMany) return { error: ADD_TASK_ERROR.TOO_MANY };
    const row = companyRow(cleaned, TASK_CHECK_STATUS.PENDING);
    return { rows: [row, ...current], key: row.key, label: row.label };
};

export const isTaskCheckPending = (row) =>
    row?.source === TASK_SOURCE.COMPANY &&
    row.checkStatus === TASK_CHECK_STATUS.PENDING;

export const hasPendingTaskChecks = (rows) =>
    (rows || []).some(isTaskCheckPending);

/**
 * Body of the check call: `jobTitle` only when a qualification is picked,
 * `existingTasks` only when the list holds a task. Pending tasks are left
 * out: their own check may still refuse them.
 */
export const buildTaskCheckBody = (label, jobTitle, rows) => {
    const body = { label };
    const title = cleanTaskLabel(jobTitle).slice(
        0,
        TASK_CHECK_JOB_TITLE_MAX_LENGTH
    );
    if (title) body.jobTitle = title;
    const existingTasks = (rows || [])
        .filter((row) => !isTaskCheckPending(row))
        .map((row) => cleanTaskLabel(row.label).slice(0, TASK_LABEL_MAX_LENGTH))
        .filter(Boolean)
        .slice(0, TASK_CHECK_EXISTING_MAX_COUNT);
    if (existingTasks.length > 0) body.existingTasks = existingTasks;
    return body;
};

// A label from the api, cleaned as the api stores it; "" when unusable.
const usableAnswerLabel = (label) => {
    const cleaned = cleanTaskLabel(label);
    return cleaned.length > TASK_LABEL_MAX_LENGTH ? "" : cleaned;
};

const fitsTaskLabel = (label) =>
    !!label && label.length <= TASK_LABEL_MAX_LENGTH;

/**
 * Answer `multiple`: the pending row is replaced by the items, in the input
 * order and at its place. Returns `{ rows, notice? }`, or `null` when no item
 * could be used (then the answer counts as unavailable).
 */
const applyMultipleTaskCheck = (current, key, items) => {
    let insertAt = current.findIndex((candidate) => candidate.key === key);
    let working = current.filter((candidate) => candidate.key !== key);
    const lines = [];
    let isTooMany = false;
    let isUsed = false;
    const isFull = () => countSelectedTasks(working) >= TASKS_MAX_COUNT;

    const markListed = (target, addLabel) => {
        isUsed = true;
        // Ticking adds one task to the payload, which the api caps.
        if (isUncheckedProposal(target) && isFull()) {
            isTooMany = true;
            return;
        }
        const result = markExistingTask(working, target, addLabel);
        working = result.rows;
        lines.push(result.notice);
    };

    const addVerified = (label) => {
        const listed = findRowByLabel(working, label);
        if (listed) {
            markListed(listed);
            return;
        }
        isUsed = true;
        if (isFull()) {
            isTooMany = true;
            return;
        }
        working = [
            ...working.slice(0, insertAt),
            companyRow(label),
            ...working.slice(insertAt),
        ];
        insertAt += 1;
    };

    items.slice(0, TASK_CHECK_ITEMS_MAX_COUNT).forEach((item) => {
        if (item?.status === "rejected") {
            const text = cleanTaskLabel(item.text);
            if (!text) return;
            isUsed = true;
            lines.push({ type: TASK_NOTICE.REJECTED, label: text });
            return;
        }
        const label = usableAnswerLabel(item?.label);
        if (item?.status === "existing") {
            const target =
                typeof item.match === "string"
                    ? findRowByLabel(working, item.match)
                    : null;
            if (target) {
                // The whole input is no task of its own: only the item
                // label can feed the "add as a new task" link.
                markListed(target, label || null);
                return;
            }
        } else if (item?.status !== "accepted") {
            return;
        }
        if (label) addVerified(label);
    });

    if (!isUsed) return null;
    if (isTooMany && lines.length === 0) {
        return { rows: working, notice: { type: TASK_NOTICE.TOO_MANY } };
    }
    if (isTooMany) lines.push({ type: TASK_NOTICE.TOO_MANY });
    // Every task simply added: the previous banner, if any, stays.
    if (lines.length === 0) return { rows: working };
    return {
        rows: working,
        notice: {
            type: TASK_NOTICE.MULTIPLE,
            lines: lines.map((line, index) => ({ ...line, id: index })),
        },
    };
};

/**
 * Applies the check answer to the row `key` only. Returns `{ rows }`, plus
 * `{ notice }` when the row is dropped. Anything but a usable `accepted`,
 * `existing`, `multiple` or a `rejected` (unavailable, unknown status,
 * network error, timeout, odd body) keeps the typed label, unless it is too
 * long for one task: then nothing is added. A row removed meanwhile is not
 * recreated.
 */
export const applyTaskCheckResult = (rows, key, result) => {
    const current = rows || [];
    const row = current.find(
        (candidate) => candidate.key === key && isTaskCheckPending(candidate)
    );
    if (!row) return { rows: current };

    const withoutRow = current.filter((candidate) => candidate.key !== key);
    const settle = (label) =>
        current.map((candidate) =>
            candidate.key === key
                ? {
                      ...candidate,
                      label,
                      checkStatus: TASK_CHECK_STATUS.VERIFIED,
                  }
                : candidate
        );
    const keepTypedLabel = () =>
        fitsTaskLabel(row.label)
            ? { rows: settle(row.label) }
            : {
                  rows: withoutRow,
                  notice: {
                      type: TASK_NOTICE.UNCHECKED_TOO_LONG,
                      label: row.label,
                  },
              };

    if (result?.status === "rejected") {
        return {
            rows: withoutRow,
            notice: { type: TASK_NOTICE.REJECTED, label: row.label },
        };
    }

    if (result?.status === "multiple") {
        const applied = Array.isArray(result.items)
            ? applyMultipleTaskCheck(current, key, result.items)
            : null;
        return applied || keepTypedLabel();
    }

    const answerLabel = usableAnswerLabel(result?.label);
    if (result?.status === "existing") {
        // Looked up in the current list: it may have changed since the call.
        const target =
            typeof result.match === "string"
                ? findRowByLabel(current, result.match, key)
                : null;
        if (target) {
            return markExistingTask(
                withoutRow,
                target,
                answerLabel || (fitsTaskLabel(row.label) ? row.label : null)
            );
        }
    } else if (result?.status !== "accepted") {
        return keepTypedLabel();
    }

    if (!answerLabel) return keepTypedLabel();
    const listed = findRowByLabel(current, answerLabel, key);
    if (listed) return markExistingTask(withoutRow, listed);
    return { rows: settle(answerLabel) };
};

/**
 * The "add as a new task" link of a notice, or of one line of a MULTIPLE
 * notice, shows only while its label is in no row: the list may have changed
 * since the notice.
 */
export const canAddNoticeTask = (rows, notice) =>
    (notice?.type === TASK_NOTICE.TICKED ||
        notice?.type === TASK_NOTICE.ALREADY_LISTED) &&
    fitsTaskLabel(cleanTaskLabel(notice.addLabel)) &&
    !findRowByLabel(rows, notice.addLabel);

/**
 * Click on the link: the user says it is another task. Adds `addLabel` as a
 * verified company task (no new AI check) and unticks the proposal the
 * notice ticked. Returns `{ rows }` or `{ error }`.
 */
export const addNoticeTask = (rows, notice) => {
    const current = rows || [];
    if (!canAddNoticeTask(current, notice)) return { rows: current };
    const unticked = notice.tickedKey
        ? setProposalChecked(current, notice.tickedKey, false)
        : current;
    if (countSelectedTasks(unticked) >= TASKS_MAX_COUNT) {
        return { error: ADD_TASK_ERROR.TOO_MANY };
    }
    // Added tasks come first in the list: the new one heads them.
    return { rows: [companyRow(cleanTaskLabel(notice.addLabel)), ...unticked] };
};

/**
 * Click on the link of one line of a MULTIPLE notice: same as
 * `addNoticeTask` for that line only, which then leaves the notice (no line
 * left, no notice). Returns `{ rows, notice }` or `{ error }`.
 */
export const addNoticeLineTask = (rows, notice, lineId) => {
    const current = rows || [];
    const lines =
        notice?.type === TASK_NOTICE.MULTIPLE && Array.isArray(notice.lines)
            ? notice.lines
            : [];
    const line = lines.find((candidate) => candidate.id === lineId);
    if (!line || !canAddNoticeTask(current, line)) {
        return { rows: current, notice };
    }
    const result = addNoticeTask(current, line);
    if (result.error) return result;
    const remaining = lines.filter((candidate) => candidate.id !== lineId);
    return {
        rows: result.rows,
        notice: remaining.length > 0 ? { ...notice, lines: remaining } : null,
    };
};

export const removeCompanyTask = (rows, key) =>
    (rows || []).filter(
        (row) => !(row.key === key && row.source === TASK_SOURCE.COMPANY)
    );

export const toggleProposedTask = (rows, key) =>
    (rows || []).map((row) =>
        row.key === key && row.source === TASK_SOURCE.QUALIFICATION
            ? { ...row, checked: !row.checked }
            : row
    );

const isSelected = (row) =>
    row.source === TASK_SOURCE.COMPANY || !!row.checked;

export const countSelectedTasks = (rows) =>
    (rows || []).filter(isSelected).length;

/**
 * Payload `tasks`: added tasks in display order, then the checked
 * qualification tasks in the qualification order.
 */
export const buildTasksPayload = (rows) => {
    // A pending input longer than one task is never kept (it would be
    // dropped on its answer), and the api would refuse the whole payload.
    const selected = (rows || []).filter(
        (row) =>
            isSelected(row) &&
            !(isTaskCheckPending(row) && !fitsTaskLabel(row.label))
    );
    const added = selected
        .filter((row) => row.source === TASK_SOURCE.COMPANY)
        .map((row) => ({ label: row.label, source: TASK_SOURCE.COMPANY }));
    const proposed = selected
        .filter((row) => row.source === TASK_SOURCE.QUALIFICATION)
        .map((row) => ({
            label: row.label,
            source: TASK_SOURCE.QUALIFICATION,
            ...(row.qualificationTaskId
                ? { qualificationTaskId: row.qualificationTaskId }
                : {}),
        }));
    return [...added, ...proposed];
};

/** Labels shown in summaries, as typed (no case change: "CACES" stays). */
export const getTemplateTaskLabels = (tasks) =>
    (Array.isArray(tasks) ? tasks : [])
        .map((task) => cleanTaskLabel(task?.label))
        .filter(Boolean);

// One pass, so a label containing "{second}" or "$&" is shown as typed.
const fillPlaceholders = (template, values) =>
    template.replace(/\{(\w+)\}/g, (match, name) =>
        Object.prototype.hasOwnProperty.call(values, name) ? values[name] : match
    );

/** "A" · "A et B" · "A, B et 1 autre" · "A, B et 4 autres"; "" when empty. */
export const formatTaskSummary = (labels, language) => {
    const list = Array.isArray(labels) ? labels.filter(Boolean) : [];
    if (list.length === 0) return "";
    if (list.length === 1) return list[0];
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.summary.${key}`, fallback);
    if (list.length === 2) {
        return fillPlaceholders(t("tasksTwo", "{first} et {second}"), {
            first: list[0],
            second: list[1],
        });
    }
    const others = list.length - 2;
    const template =
        others === 1
            ? t("tasksManyOne", "{first}, {second} et {count} autre")
            : t("tasksMany", "{first}, {second} et {count} autres");
    return fillPlaceholders(template, {
        first: list[0],
        second: list[1],
        count: String(others),
    });
};
